import React, { useEffect, useState } from "react";
import axios from "axios";

const api = axios.create({
    baseURL: "/api/admin/popups",
    headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function getImageUrl(popup) {
    return `/api/public/popups/${popup._id}/image`;
}

export default function Popups() {
    const [popups, setPopups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [form, setForm] = useState({
        type: "text",
        text: "",
        image: null,
        startDate: "",
        endDate: "",
    });
    const [editingId, setEditingId] = useState(null);

    const fetchPopups = async () => {
        setLoading(true);
        try {
            const res = await api.get("/");
            setPopups(res.data);
        } catch (err) {
            setError("Fehler beim Laden der Popups");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPopups();
    }, []);

    const handleChange = (e) => {
        const { name, value, type: inputType, files } = e.target;
        if (inputType === "file") {
            setForm((f) => ({ ...f, image: files[0] }));
        } else {
            setForm((f) => ({ ...f, [name]: value }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            let data;
            if (form.type === "image" && form.image instanceof File) {
                data = new FormData();
                data.append("type", form.type);
                data.append("image", form.image);
                data.append("startDate", form.startDate);
                data.append("endDate", form.endDate);
            } else {
                data = {
                    type: form.type,
                    text: form.text,
                    startDate: form.startDate,
                    endDate: form.endDate,
                };
            }
            if (editingId) {
                await api.put(
                    `/${editingId}`,
                    data,
                    form.type === "image"
                        ? { headers: { "Content-Type": "multipart/form-data" } }
                        : {}
                );
            } else {
                await api.post(
                    `/`,
                    data,
                    form.type === "image"
                        ? { headers: { "Content-Type": "multipart/form-data" } }
                        : {}
                );
            }
            setForm({
                type: "text",
                text: "",
                image: null,
                startDate: "",
                endDate: "",
            });
            setEditingId(null);
            fetchPopups();
        } catch (err) {
            setError("Fehler beim Speichern des Popups");
        }
    };

    const handleEdit = (popup) => {
        setForm({
            type: popup.type,
            text: popup.text || "",
            image: null,
            startDate: popup.startDate ? popup.startDate.slice(0, 10) : "",
            endDate: popup.endDate ? popup.endDate.slice(0, 10) : "",
        });
        setEditingId(popup._id);
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Popup wirklich löschen?")) return;
        try {
            await api.delete(`/${id}`);
            fetchPopups();
        } catch (err) {
            setError("Fehler beim Löschen des Popups");
        }
    };

    return (
        <div className="max-w-2xl mx-auto bg-white p-6 rounded-md shadow-md mt-16">
            <h1 className="text-2xl font-bold mb-4">Popups verwalten</h1>
            {error && <div className="text-red-500 mb-2">{error}</div>}
            <form onSubmit={handleSubmit} className="mb-8 space-y-4">
                <div>
                    <label className="font-medium">Typ:</label>
                    <select
                        name="type"
                        value={form.type}
                        onChange={handleChange}
                        className="ml-2 border rounded p-1"
                    >
                        <option value="text">Text</option>
                        <option value="image">Bild</option>
                    </select>
                </div>
                {form.type === "text" ? (
                    <div>
                        <label className="font-medium">Text:</label>
                        <input
                            name="text"
                            value={form.text}
                            onChange={handleChange}
                            className="ml-2 border rounded p-1 w-full"
                            required={form.type === "text"}
                        />
                    </div>
                ) : (
                    <div>
                        <label className="font-medium">Bild:</label>
                        <input
                            name="image"
                            type="file"
                            accept="image/*"
                            onChange={handleChange}
                            className="ml-2"
                            required={form.type === "image"}
                        />
                    </div>
                )}
                <div>
                    <label className="font-medium">Von:</label>
                    <input
                        name="startDate"
                        type="date"
                        value={form.startDate}
                        onChange={handleChange}
                        className="ml-2 border rounded p-1"
                        required
                    />
                    <label className="font-medium ml-4">Bis:</label>
                    <input
                        name="endDate"
                        type="date"
                        value={form.endDate}
                        onChange={handleChange}
                        className="ml-2 border rounded p-1"
                        required
                    />
                </div>
                <button
                    type="submit"
                    className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                    {editingId ? "Popup aktualisieren" : "Popup anlegen"}
                </button>
                {editingId && (
                    <button
                        type="button"
                        className="ml-2 bg-gray-300 px-4 py-2 rounded"
                        onClick={() => {
                            setEditingId(null);
                            setForm({
                                type: "text",
                                text: "",
                                image: null,
                                startDate: "",
                                endDate: "",
                            });
                        }}
                    >
                        Abbrechen
                    </button>
                )}
            </form>
            <h2 className="text-lg font-bold mb-2">Alle Popups</h2>
            {loading ? (
                <div>Lädt...</div>
            ) : (
                <ul className="space-y-4">
                    {popups.map((popup) => (
                        <li
                            key={popup._id}
                            className="border rounded p-4 flex flex-col md:flex-row md:items-center md:justify-between"
                        >
                            <div>
                                <div className="font-semibold">
                                    Typ: {popup.type}
                                </div>
                                {popup.type === "text" ? (
                                    <div>Text: {popup.text}</div>
                                ) : (
                                    <div>
                                        Bild:{" "}
                                        <img
                                            src={getImageUrl(popup)}
                                            alt="Popup"
                                            className="h-16 inline"
                                        />
                                    </div>
                                )}
                                <div>
                                    Von:{" "}
                                    {new Date(
                                        popup.startDate
                                    ).toLocaleDateString()}{" "}
                                    bis{" "}
                                    {new Date(
                                        popup.endDate
                                    ).toLocaleDateString()}
                                </div>
                            </div>
                            <div className="flex gap-2 mt-2 md:mt-0">
                                <button
                                    className="bg-yellow-400 px-3 py-1 rounded"
                                    onClick={() => handleEdit(popup)}
                                >
                                    Bearbeiten
                                </button>
                                <button
                                    className="bg-red-500 text-white px-3 py-1 rounded"
                                    onClick={() => handleDelete(popup._id)}
                                >
                                    Löschen
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
